import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import AdminLayout from '../components/Layout/AdminLayout';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import { galleryAPI, uploadFile } from '../services/api';
import { Plus, Trash2, Image as ImageIcon, Video, Upload, FolderPlus, Edit, Settings } from 'lucide-react';

const Gallery = () => {
  const [mediaItems, setMediaItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editCategoryModalOpen, setEditCategoryModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteCategoryConfirm, setDeleteCategoryConfirm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      category: 'mosque'
    }
  });
  const { register: registerCategory, handleSubmit: handleSubmitCategory, reset: resetCategory, formState: { errors: categoryErrors } } = useForm();
  const { register: registerEditCategory, handleSubmit: handleSubmitEditCategory, reset: resetEditCategory, setValue: setEditCategoryValue, formState: { errors: editCategoryErrors } } = useForm();
  const watchFile = watch('file');

  useEffect(() => {
    fetchMedia();
    fetchCategories();
  }, [filterType, filterCategory]);

  const fetchCategories = async () => {
    try {
      const response = await galleryAPI.getAllCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchMedia = async () => {
    try {
      const params = {};
      if (filterType !== 'all') params.type = filterType;
      if (filterCategory !== 'all') params.category = filterCategory;

      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `?${queryString}` : '';
      const response = await galleryAPI.getAll(url);
      setMediaItems(response.data || []);
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error('فشل في تحميل المعرض');
    } finally {
      setLoading(false);
    }
  };

  const uploadMedia = async (data) => {
    if (!data.file || data.file.length === 0) {
      toast.error('الرجاء اختيار ملف');
      return;
    }

    setUploading(true);
    try {
      const file = data.file[0];
      const fileType = file.type.startsWith('image/') ? 'image' : 'video';

      const fileUrl = await uploadFile(file, 'gallery');

      await galleryAPI.create({
        title: data.title,
        description: data.description || '',
        url: fileUrl,
        type: fileType,
        filePath: fileUrl,
        category: data.category || 'mosque',
      });

      toast.success('تم رفع الملف بنجاح');
      setUploadModalOpen(false);
      reset();
      fetchMedia();
    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error('فشل في رفع الملف');
    } finally {
      setUploading(false);
    }
  };

  const deleteMedia = async (id) => {
    try {
      await galleryAPI.delete(id);
      toast.success('تم حذف الملف بنجاح');
      setDeleteConfirm(null);
      fetchMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('فشل في حذف الملف');
    }
  };

  const createCategory = async (data) => {
    try {
      await galleryAPI.createCategory({
        key: data.key,
        nameAr: data.nameAr,
        descriptionAr: data.descriptionAr || '',
        color: data.color || 'blue',
        sortOrder: parseInt(data.sortOrder) || 0,
      });
      toast.success('تم إضافة التصنيف بنجاح');
      setCategoryModalOpen(false);
      resetCategory();
      fetchCategories();
      fetchMedia();
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error(error.response?.data?.message || 'فشل في إضافة التصنيف');
    }
  };

  const updateCategory = async (data) => {
    try {
      await galleryAPI.updateCategory(selectedCategory.id, {
        key: data.key,
        nameAr: data.nameAr,
        descriptionAr: data.descriptionAr || '',
        color: data.color || 'blue',
        sortOrder: parseInt(data.sortOrder) || 0,
      });
      toast.success('تم تحديث التصنيف بنجاح');
      setEditCategoryModalOpen(false);
      setSelectedCategory(null);
      resetEditCategory();
      fetchCategories();
      fetchMedia();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error(error.response?.data?.message || 'فشل في تحديث التصنيف');
    }
  };

  const deleteCategory = async (id) => {
    try {
      await galleryAPI.deleteCategory(id);
      toast.success('تم حذف التصنيف بنجاح');
      setDeleteCategoryConfirm(null);
      fetchCategories();
      fetchMedia();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.message || 'فشل في حذف التصنيف');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">معرض الوسائط</h1>
            <p className="text-text-secondary">إدارة الصور والفيديوهات</p>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
            >
              <option value="all">جميع التصنيفات</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.key}>{cat.nameAr}</option>
              ))}
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
            >
              <option value="all">جميع الوسائط</option>
              <option value="image">الصور</option>
              <option value="video">الفيديوهات</option>
            </select>
            <Button variant="ghost" onClick={() => setCategoryModalOpen(true)}>
              <Settings className="w-4 h-4 ml-1" />
              إدارة التصنيفات
            </Button>
            <Button onClick={() => setUploadModalOpen(true)}>
              <Plus className="w-4 h-4 ml-1" />
              رفع ملف جديد
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((cat) => (
            <div key={cat.id} className="bg-card rounded-xl p-6 border border-divider">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-text-secondary text-sm font-medium">{cat.nameAr}</p>
                  <p className="text-2xl font-bold text-text-primary mt-2">
                    {mediaItems.filter(m => m.category === cat.key).length}
                  </p>
                </div>
                <div className={`bg-${cat.color}-100 p-3 rounded-lg`}>
                  <ImageIcon className={`h-6 w-6 text-${cat.color}-600`} />
                </div>
              </div>
            </div>
          ))}

          <div className="bg-card rounded-xl p-6 border border-divider">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-secondary text-sm font-medium">إجمالي الوسائط</p>
                <p className="text-2xl font-bold text-text-primary mt-2">
                  {mediaItems.length}
                </p>
              </div>
              <div className="bg-orange-100 p-3 rounded-lg">
                <Video className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {mediaItems.length === 0 ? (
          <div className="bg-card rounded-xl border border-divider p-12 text-center">
            <Upload className="mx-auto h-12 w-12 text-text-muted mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">لا توجد وسائط</h3>
            <p className="text-text-muted mb-4">
              {filterType === 'all'
                ? 'ابدأ برفع الصور والفيديوهات إلى المعرض'
                : `لا توجد ${filterType === 'image' ? 'صور' : 'فيديوهات'} في المعرض`
              }
            </p>
            <Button onClick={() => setUploadModalOpen(true)}>
              <Plus className="w-4 h-4 ml-1" />
              رفع ملف جديد
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mediaItems.map((item) => (
              <div
                key={item.id}
                className="bg-card rounded-xl border border-divider overflow-hidden hover:border-accent-gold transition-colors group"
              >
                <div
                  className="relative aspect-video bg-primary-light cursor-pointer"
                  onClick={() => {
                    setSelectedMedia(item);
                    setViewModalOpen(true);
                  }}
                >
                  {item.type === 'image' ? (
                    <img
                      src={item.url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video className="w-12 h-12 text-accent-gold" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                    <div className="transform scale-0 group-hover:scale-100 transition-transform">
                      {item.type === 'image' ? (
                        <ImageIcon className="w-8 h-8 text-white" />
                      ) : (
                        <Video className="w-8 h-8 text-white" />
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-text-primary truncate flex-1">{item.title}</h3>
                    {(() => {
                      const cat = categories.find(c => c.key === item.category);
                      return cat ? (
                        <span className={`text-xs px-2 py-1 rounded-full bg-${cat.color}-100 text-${cat.color}-700`}>
                          {cat.nameAr}
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                          {item.category}
                        </span>
                      );
                    })()}
                  </div>
                  {item.description && (
                    <p className="text-sm text-text-muted mb-3 line-clamp-2">{item.description}</p>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t border-divider">
                    <span className="text-xs text-text-muted">
                      {new Date(item.createdAt).toLocaleDateString('ar-SA')}
                    </span>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setDeleteConfirm(item)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          reset();
        }}
        title="رفع ملف جديد"
        size="lg"
      >
        <form onSubmit={handleSubmit(uploadMedia)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              العنوان *
            </label>
            <input
              type="text"
              {...register('title', { required: 'العنوان مطلوب' })}
              className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              placeholder="أدخل عنوان الملف"
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              الوصف
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              placeholder="وصف اختياري للملف"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              التصنيف *
            </label>
            <select
              {...register('category', { required: 'التصنيف مطلوب' })}
              className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.key}>{cat.nameAr}</option>
              ))}
            </select>
            {errors.category && (
              <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              الملف * (صورة أو فيديو)
            </label>
            <input
              type="file"
              {...register('file', { required: 'الملف مطلوب' })}
              accept="image/*,video/*"
              className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-accent-gold file:text-white hover:file:bg-opacity-90"
            />
            {errors.file && (
              <p className="text-red-500 text-sm mt-1">{errors.file.message}</p>
            )}
            {watchFile && watchFile.length > 0 && (
              <p className="text-sm text-text-muted mt-2">
                {watchFile[0].type.startsWith('image/') ? 'صورة' : 'فيديو'}: {watchFile[0].name}
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-3 space-x-reverse pt-4 border-t border-divider">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setUploadModalOpen(false);
                reset();
              }}
            >
              إلغاء
            </Button>
            <Button type="submit" disabled={uploading}>
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-1"></div>
                  جاري الرفع...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 ml-1" />
                  رفع الملف
                </>
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        title={selectedMedia?.title}
        size="xl"
      >
        {selectedMedia && (
          <div className="space-y-4">
            {selectedMedia.type === 'image' ? (
              <img
                src={selectedMedia.url}
                alt={selectedMedia.title}
                className="w-full rounded-lg"
              />
            ) : (
              <video
                src={selectedMedia.url}
                controls
                className="w-full rounded-lg"
              >
                متصفحك لا يدعم تشغيل الفيديو
              </video>
            )}
            {selectedMedia.description && (
              <div>
                <h4 className="text-sm font-medium text-text-primary mb-1">الوصف</h4>
                <p className="text-text-secondary">{selectedMedia.description}</p>
              </div>
            )}
            <div className="pt-3 border-t border-divider space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-muted">التصنيف:</span>
                {(() => {
                  const cat = categories.find(c => c.key === selectedMedia.category);
                  return cat ? (
                    <span className={`text-xs px-2 py-1 rounded-full bg-${cat.color}-100 text-${cat.color}-700`}>
                      {cat.nameAr}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                      {selectedMedia.category}
                    </span>
                  );
                })()}
              </div>
              <p className="text-sm text-text-muted">
                تاريخ الإضافة: {new Date(selectedMedia.createdAt).toLocaleDateString('ar-SA')}
              </p>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="تأكيد الحذف"
      >
        <div className="space-y-4">
          <p className="text-text-secondary">
            هل أنت متأكد من حذف "{deleteConfirm?.title}"؟ لا يمكن التراجع عن هذا الإجراء.
          </p>
          <div className="flex justify-end space-x-3 space-x-reverse pt-4 border-t border-divider">
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
              إلغاء
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteMedia(deleteConfirm.id)}
            >
              <Trash2 className="w-4 h-4 ml-1" />
              حذف
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={categoryModalOpen}
        onClose={() => {
          setCategoryModalOpen(false);
          resetCategory();
        }}
        title="إدارة التصنيفات"
        size="xl"
      >
        <div className="space-y-6">
          <form onSubmit={handleSubmitCategory(createCategory)} className="space-y-4 p-4 bg-primary-light rounded-lg">
            <h3 className="font-semibold text-text-primary mb-3">إضافة تصنيف جديد</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  المفتاح * (بالإنجليزية)
                </label>
                <input
                  type="text"
                  {...registerCategory('key', { required: 'المفتاح مطلوب' })}
                  className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                  placeholder="مثال: events"
                />
                {categoryErrors.key && (
                  <p className="text-red-500 text-sm mt-1">{categoryErrors.key.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  الاسم بالعربية *
                </label>
                <input
                  type="text"
                  {...registerCategory('nameAr', { required: 'الاسم مطلوب' })}
                  className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                  placeholder="مثال: صور الفعاليات"
                />
                {categoryErrors.nameAr && (
                  <p className="text-red-500 text-sm mt-1">{categoryErrors.nameAr.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  الوصف
                </label>
                <input
                  type="text"
                  {...registerCategory('descriptionAr')}
                  className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                  placeholder="وصف اختياري"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  اللون
                </label>
                <select
                  {...registerCategory('color')}
                  className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                >
                  <option value="blue">أزرق</option>
                  <option value="green">أخضر</option>
                  <option value="purple">بنفسجي</option>
                  <option value="red">أحمر</option>
                  <option value="yellow">أصفر</option>
                  <option value="pink">وردي</option>
                  <option value="orange">برتقالي</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  ترتيب العرض
                </label>
                <input
                  type="number"
                  {...registerCategory('sortOrder')}
                  defaultValue={0}
                  className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit">
                <Plus className="w-4 h-4 ml-1" />
                إضافة التصنيف
              </Button>
            </div>
          </form>

          <div className="space-y-3">
            <h3 className="font-semibold text-text-primary">التصنيفات الحالية</h3>
            {categories.length === 0 ? (
              <p className="text-text-muted text-center py-8">لا توجد تصنيفات</p>
            ) : (
              categories.map((cat) => (
                <div key={cat.id} className="flex items-center justify-between p-4 bg-card border border-divider rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg bg-${cat.color}-100 flex items-center justify-center`}>
                      <ImageIcon className={`w-5 h-5 text-${cat.color}-600`} />
                    </div>
                    <div>
                      <p className="font-semibold text-text-primary">{cat.nameAr}</p>
                      <p className="text-sm text-text-muted">{cat.key} • {cat.descriptionAr || 'بدون وصف'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedCategory(cat);
                        setEditCategoryValue('key', cat.key);
                        setEditCategoryValue('nameAr', cat.nameAr);
                        setEditCategoryValue('descriptionAr', cat.descriptionAr);
                        setEditCategoryValue('color', cat.color);
                        setEditCategoryValue('sortOrder', cat.sortOrder);
                        setEditCategoryModalOpen(true);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => setDeleteCategoryConfirm(cat)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={editCategoryModalOpen}
        onClose={() => {
          setEditCategoryModalOpen(false);
          setSelectedCategory(null);
          resetEditCategory();
        }}
        title="تعديل التصنيف"
        size="lg"
      >
        <form onSubmit={handleSubmitEditCategory(updateCategory)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                المفتاح * (بالإنجليزية)
              </label>
              <input
                type="text"
                {...registerEditCategory('key', { required: 'المفتاح مطلوب' })}
                className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              />
              {editCategoryErrors.key && (
                <p className="text-red-500 text-sm mt-1">{editCategoryErrors.key.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                الاسم بالعربية *
              </label>
              <input
                type="text"
                {...registerEditCategory('nameAr', { required: 'الاسم مطلوب' })}
                className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              />
              {editCategoryErrors.nameAr && (
                <p className="text-red-500 text-sm mt-1">{editCategoryErrors.nameAr.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                الوصف
              </label>
              <input
                type="text"
                {...registerEditCategory('descriptionAr')}
                className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                اللون
              </label>
              <select
                {...registerEditCategory('color')}
                className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              >
                <option value="blue">أزرق</option>
                <option value="green">أخضر</option>
                <option value="purple">بنفسجي</option>
                <option value="red">أحمر</option>
                <option value="yellow">أصفر</option>
                <option value="pink">وردي</option>
                <option value="orange">برتقالي</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                ترتيب العرض
              </label>
              <input
                type="number"
                {...registerEditCategory('sortOrder')}
                className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 space-x-reverse pt-4 border-t border-divider">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setEditCategoryModalOpen(false);
                setSelectedCategory(null);
                resetEditCategory();
              }}
            >
              إلغاء
            </Button>
            <Button type="submit">
              حفظ التغييرات
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!deleteCategoryConfirm}
        onClose={() => setDeleteCategoryConfirm(null)}
        title="تأكيد الحذف"
      >
        <div className="space-y-4">
          <p className="text-text-secondary">
            هل أنت متأكد من حذف تصنيف "{deleteCategoryConfirm?.nameAr}"؟ لن تتمكن من حذف التصنيف إذا كان يحتوي على صور.
          </p>
          <div className="flex justify-end space-x-3 space-x-reverse pt-4 border-t border-divider">
            <Button variant="ghost" onClick={() => setDeleteCategoryConfirm(null)}>
              إلغاء
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteCategory(deleteCategoryConfirm.id)}
            >
              <Trash2 className="w-4 h-4 ml-1" />
              حذف
            </Button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default Gallery;
